import { AgentConfig } from '../types/agentConfig';

/**
 * Registry for different agent configurations
 */
class AgentConfigRegistry {
  private configs: Map<string, AgentConfig> = new Map();

  /**
   * Register an agent configuration
   */
  register(config: AgentConfig): void {
    this.configs.set(config.agentType, config);
  }

  /**
   * Get agent configuration by type
   */
  getConfig(agentType: string): AgentConfig {
    const config = this.configs.get(agentType);
    if (!config) {
      throw new Error(`Agent configuration not found for type: ${agentType}`);
    }
    return config;
  }

  /**
   * Check if agent type is registered
   */
  hasConfig(agentType: string): boolean {
    return this.configs.has(agentType);
  }

  /**
   * Get all registered agent types
   */
  getRegisteredTypes(): string[] {
    return Array.from(this.configs.keys());
  }
}

// Export singleton instance
export const agentConfigRegistry = new AgentConfigRegistry();

// Document agent configuration
export const documentAgentConfig: AgentConfig = {
  agentType: 'document',
  displayName: 'Document Assistant',
  getConversationsPath: (documentId: string) => 
    `documents/${documentId}/document_agent_conversations`,
  getMessagesPath: (documentId: string, conversationId: string) => 
    `documents/${documentId}/document_agent_conversations/${conversationId}/messages`,
  analyticsPrefix: 'document_agent'
};

// Property agent configuration
export const propertyAgentConfig: AgentConfig = {
  agentType: 'property',
  displayName: 'Property Assistant',
  getConversationsPath: (propertyId: string, userId?: string) => {
    if (!userId) {
      throw new Error('userId is required for property agent conversations');
    }
    return `users/${userId}/properties/${propertyId}/agent_conversations`;
  },
  getMessagesPath: (propertyId: string, conversationId: string, userId?: string) => {
    if (!userId) {
      throw new Error('userId is required for property agent messages');
    }
    return `users/${userId}/properties/${propertyId}/agent_conversations/${conversationId}/messages`;
  },
  analyticsPrefix: 'property_agent'
};

// Test agent configuration (for sandbox/development)
export const testAgentConfig: AgentConfig = {
  agentType: 'test',
  displayName: 'Test Assistant',
  getConversationsPath: () => 
    `test_agent_conversations`,
  getMessagesPath: (_testId: string, conversationId: string) => 
    `test_agent_conversations/${conversationId}/messages`,
  analyticsPrefix: 'test_agent'
};

// Register default configurations
agentConfigRegistry.register(documentAgentConfig);
agentConfigRegistry.register(propertyAgentConfig);

// Register test agent only in development mode
if (process.env.NODE_ENV === 'development') {
  agentConfigRegistry.register(testAgentConfig);
}